import { useState } from 'react';
import { ChevronDown, ChevronRight, Folder, FolderOpen, FileText, Trash2, Plus } from 'lucide-react';

const CHEVRON_DOWN = <ChevronDown width="12" height="12" strokeWidth={2} />;
const CHEVRON_RIGHT = <ChevronRight width="12" height="12" strokeWidth={2} />;
const FOLDER_ICON = <Folder width="16" height="16" strokeWidth={2} />;
const FOLDER_OPEN_ICON = <FolderOpen width="16" height="16" strokeWidth={2} />;
const ALL_DOCS_ICON = <FileText width="16" height="16" strokeWidth={2} />;
const TRASH_ICON = <Trash2 width="12" height="12" strokeWidth={2} />;
const PLUS_ICON = <Plus width="14" height="14" strokeWidth={2} />;

function normalizeTree(nodes) {
  return (nodes || []).map(function (c) {
    return {
      ...c,
      children: normalizeTree(c.sottocartelle),
    };
  });
}

function TreeNode({ node, selectedId, onSelect, onDelete, depth }) {
  var _a;
  const [expanded, setExpanded] = useState(depth === 0);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedId === node.id;

  return (
    <div>
      <div
        onClick={function () { onSelect(node.id); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '12px 8px', paddingLeft: 8 + depth * 16,
          borderRadius: 'var(--radius-base)', cursor: 'pointer',
          background: isSelected ? 'color-mix(in srgb, var(--brand) 12%, var(--ds-bg))' : 'transparent',
          color: isSelected ? 'var(--brand)' : 'var(--ds-text)',
          fontSize: 13, fontWeight: isSelected ? 500 : 400,
          transition: 'background 0.15s',
        }}
        onMouseEnter={function (e) { if (!isSelected) e.currentTarget.style.background = 'var(--chip)'; }}
        onMouseLeave={function (e) { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
      >
        {hasChildren ? (
          <span
            onClick={function (e) { e.stopPropagation(); setExpanded(!expanded); }}
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: 'var(--ds-muted)' }}
          >
            {expanded ? CHEVRON_DOWN : CHEVRON_RIGHT}
          </span>
        ) : (
          <span style={{ width: 12 }} />
        )}
        <span style={{ display: 'flex', alignItems: 'center', color: isSelected ? 'var(--brand)' : 'var(--ds-muted)' }}>
          {expanded && hasChildren ? FOLDER_OPEN_ICON : FOLDER_ICON}
        </span>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{node.nome}</span>
        {((_a = node.documenti_count) != null ? _a : null) !== null && (
          <span title="Totale documenti in cartella (incluse sottocartelle)" style={{ fontSize: 10, color: 'var(--ds-muted)', background: 'var(--chip)', padding: '1px 6px', borderRadius: 99, fontWeight: 500 }}>
            {node.documenti_count}
          </span>
        )}
        {!node.is_predefinita && (
          <span
            onClick={function (e) {
              e.stopPropagation();
              if (window.confirm('Eliminare la cartella "' + node.nome + '"?')) onDelete(node.id);
            }}
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: 'var(--ds-muted)', opacity: 0.5 }}
            onMouseEnter={function (e) { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = 'var(--ds-danger)'; }}
            onMouseLeave={function (e) { e.currentTarget.style.opacity = '0.5'; e.currentTarget.style.color = 'var(--ds-muted)'; }}
            title="Elimina cartella"
          >
            {TRASH_ICON}
          </span>
        )}
      </div>
      {expanded && hasChildren && node.children.map(function (child) {
        return (
          <TreeNode
            key={child.id}
            node={child}
            selectedId={selectedId}
            onSelect={onSelect}
            onDelete={onDelete}
            depth={depth + 1}
          />
        );
      })}
    </div>
  );
}

function SkeletonSidebar() {
  return (
    <div style={{ padding: '8px 0' }}>
      {[1, 2, 3, 4].map(function (i) {
        return (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px' }}>
            <div style={{ width: 16, height: 16, borderRadius: 4, background: 'var(--ds-border)' }} />
            <div style={{ flex: 1, height: 12, borderRadius: 4, background: 'var(--ds-border)', opacity: 0.6 + i * 0.1 }} />
          </div>
        );
      })}
    </div>
  );
}

export default function SidebarCartelle({ cartelle, loading, selectedCartella, onSelectCartella, onDeleteCartella, onCreaCartella }) {
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  var tree = normalizeTree(cartelle || []);
  var isAll = selectedCartella === null;

  var handleCreate = async function () {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await onCreaCartella({ nome: newName.trim() });
      setNewName('');
      setShowNew(false);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div style={{
      width: 240, minWidth: 240, borderRight: '1px solid var(--ds-border)',
      background: 'var(--ds-bg)', display: 'flex', flexDirection: 'column',
      height: '100%', overflow: 'hidden',
    }}>
      <div style={{ padding: '16px 12px 8px', fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        Cartelle
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '0 4px' }}>
        {/* Tutti i documenti */}
        <div
          onClick={function () { onSelectCartella(null); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '12px 8px', borderRadius: 'var(--radius-base)', cursor: 'pointer',
            background: isAll ? 'color-mix(in srgb, var(--brand) 12%, var(--ds-bg))' : 'transparent',
            color: isAll ? 'var(--brand)' : 'var(--ds-text)',
            fontSize: 13, fontWeight: isAll ? 500 : 400,
            marginBottom: 4, transition: 'background 0.15s',
          }}
          onMouseEnter={function (e) { if (!isAll) e.currentTarget.style.background = 'var(--chip)'; }}
          onMouseLeave={function (e) { if (!isAll) e.currentTarget.style.background = 'transparent'; }}
        >
          <span style={{ width: 12 }} />
          <span style={{ display: 'flex', alignItems: 'center', color: isAll ? 'var(--brand)' : 'var(--ds-muted)' }}>{ALL_DOCS_ICON}</span>
          <span>Tutti i documenti</span>
        </div>

        {loading ? (
          <SkeletonSidebar />
        ) : (
          tree.map(function (node) {
            return (
              <TreeNode
                key={node.id}
                node={node}
                selectedId={selectedCartella}
                onSelect={onSelectCartella}
                onDelete={onDeleteCartella}
                depth={0}
              />
            );
          })
        )}
      </div>

      {/* Crea nuova cartella */}
      <div style={{ padding: '8px 8px 12px', borderTop: '1px solid var(--ds-border)' }}>
        {showNew ? (
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              autoFocus
              value={newName}
              onChange={function (e) { setNewName(e.target.value); }}
              onKeyDown={function (e) { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') { setShowNew(false); setNewName(''); } }}
              placeholder="Nome cartella..."
              style={{ flex: 1, padding: '6px 8px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 12, outline: 'none', background: 'var(--ds-bg)', color: 'var(--ds-text)', fontFamily: 'inherit' }}
            />
            <button
              onClick={handleCreate}
              disabled={creating || !newName.trim()}
              style={{ padding: '6px 10px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: creating ? 'not-allowed' : 'pointer', fontSize: 11, fontWeight: 500, opacity: creating ? 0.7 : 1 }}
            >
              {creating ? '...' : 'Crea'}
            </button>
          </div>
        ) : (
          <button
            onClick={function () { setShowNew(true); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, width: '100%',
              padding: '7px 8px', borderRadius: 'var(--radius-base)', border: '1px dashed var(--ds-border)',
              background: 'transparent', cursor: 'pointer', fontSize: 12, fontWeight: 500,
              color: 'var(--ds-muted)', transition: 'all 0.15s',
            }}
            onMouseEnter={function (e) { e.currentTarget.style.borderColor = 'var(--brand)'; e.currentTarget.style.color = 'var(--brand)'; }}
            onMouseLeave={function (e) { e.currentTarget.style.borderColor = 'var(--ds-border)'; e.currentTarget.style.color = 'var(--ds-muted)'; }}
          >
            {PLUS_ICON} Nuova cartella
          </button>
        )}
      </div>
    </div>
  );
}
